import { useAccount, useDashboardContext, useToasts } from '@src/contexts';
import useToggle from '@src/hooks/utils/useToggle';
import {
  useAddPaymentLink,
  useGetPrimarySettlementAccounts,
  useUpdatePaymentLink
} from '@src/hooks/queries/user';
import { Path } from '@src/navigations/routes';
import ClientApi from '@src/types/client';
import { getCurrencySymbol } from '@src/utils/getCurrencySymbol';
import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PaymentLinkFormII } from '../components/recurrentPaymentLinkForm';
import { getRecurrentPaymentLinkValidationSchema } from '../utils/getPaymentLinkValidationSchema';
import { intervalsData } from '../data/intervals';

interface UseRecurrentPaymentLinkFormOptions {
  paymentLinkObj?: ClientApi.General.PaymentLinkI;
  handleClose: () => void;
  shouldRedirect?: boolean;
  frequency?: number;
  variant?: 'receiver' | 'raas';
}

export interface UseRecurrentPaymentLinkFormResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: any;
  initialValues: PaymentLinkFormII;
  handleSubmit: (values: PaymentLinkFormII) => void;
  buttonLabel: string;
  title: string;
  isLoading: boolean;
  primarySettlementAccount: ClientApi.General.SettlementAccountI | undefined;
  primarySettlementAccountLoading: boolean;
  primarySettlementAccountError: boolean;
  primarySettlementAccountRefetch: () => void;
  shouldDisableForm: boolean;
  showVeriffModal: boolean;
  handleVeriffModalClose: () => void;
}

const useRecurrentPaymentLinkForm = ({
  handleClose,
  paymentLinkObj,
  shouldRedirect,
  variant
}: UseRecurrentPaymentLinkFormOptions): UseRecurrentPaymentLinkFormResponse => {
  const {
    state: showVeriffModal,
    handleFalse: handleVeriffModalClose,
    handleTrue: handleVeriffModalOpen
  } = useToggle();

  const { addToast } = useToasts();
  const queryClient = useQueryClient();
  const { userAddress, isRemittanceBusiness } = useAccount();
  const { shouldBusinessMakeTransactions } = useDashboardContext();
  const navigate = useNavigate();

  const isEdit = !!paymentLinkObj;
  const shouldDisableForm = isEdit && paymentLinkObj?.status !== 'Active';
  const accountCurrency = getCurrencySymbol(userAddress?.country);

  const {
    data: primarySettlementAccount,
    isLoading: primarySettlementAccountLoading,
    isError: primarySettlementAccountError,
    refetch: primarySettlementAccountRefetch
  } = useGetPrimarySettlementAccounts({
    enabled: variant === 'receiver'
  });

  const initialValues = useMemo<PaymentLinkFormII>(() => {
    const selectedInterval = intervalsData?.find((item) => {
      const intervalName =
        paymentLinkObj?.intervalName?.toLowerCase() === 'bi-anually'
          ? 'bi-annually'
          : paymentLinkObj?.intervalName?.toLowerCase();
      return item?.interval?.toLowerCase() === intervalName;
    });

    return {
      amount: paymentLinkObj?.amount?.toString() || '',
      description: paymentLinkObj?.description || '',
      linkName: paymentLinkObj?.name || '',
      targetCurrency:
        variant === 'raas'
          ? 'USD'
          : paymentLinkObj?.targetCurrencyCode ||
            primarySettlementAccount?.accountCurrencyCode ||
            accountCurrency,
      interval: selectedInterval?.id || '',
      intervalCount: paymentLinkObj?.duration || '',
      customerCanCancelSubscription: paymentLinkObj?.customerCanCancelSubscription || false,
      redirectUrl: paymentLinkObj?.redirectUrl || ''
    };
  }, [paymentLinkObj, primarySettlementAccount, variant]);

  const { mutate: addPaymentLinkMutate, isLoading: addSettlementLoading } = useAddPaymentLink({
    onSuccess: () => {
      handleSuccessCallback('add');
    }
  });

  const { mutate: updatePaymentLinkMutate, isLoading: updatePaymentLinkLoading } =
    useUpdatePaymentLink({
      onSuccess: () => {
        handleSuccessCallback('update');
      }
    });

  const handleSuccessCallback = (type: 'add' | 'update') => {
    handleClose();

    if (type === 'add') {
      addToast({ body: 'Payment link added', title: 'Success' });
    }
    if (type === 'update') {
      addToast({ body: 'Payment link updated', title: 'Success' });
    }

    queryClient.invalidateQueries(['getPaymentLinks']);
    queryClient.invalidateQueries(['getPaymentLinkDetails']);
    queryClient.invalidateQueries(['getPaymentLinkSummary']);

    if (shouldRedirect) {
      if (isRemittanceBusiness) {
        navigate(Path.SenderPaymentLinks);
      } else {
        navigate(Path.ReceiverPaymentLinks);
      }
    }
  };

  const handleAdd = (values: PaymentLinkFormII) => {
    addPaymentLinkMutate({
      amount: isNaN(Number(values.amount)) ? 0 : Number(values.amount),
      description: values.description,
      extraFields: [],
      name: values.linkName,
      targetCurrencyCode: values.targetCurrency,
      frequency: 2,
      interval: values.interval ? Number(values.interval) : 0,
      duration: values.intervalCount ? Number(values.intervalCount) : 0,
      customerCanCancelSubscription: values.customerCanCancelSubscription,
      redirectUrl: values.redirectUrl
    });
  };

  const handleEdit = (values: PaymentLinkFormII) => {
    updatePaymentLinkMutate({
      amount: isNaN(Number(values.amount)) ? 0 : Number(values.amount),
      description: values.description,
      extraFields: [],
      name: values.linkName,
      targetCurrencyCode: values.targetCurrency,
      frequency: 2,
      interval: values.interval ? Number(values.interval) : 0,
      redirectUrl: values.redirectUrl,
      duration: values.intervalCount ? Number(values.intervalCount) : 0,
      customerCanCancelSubscription: values.customerCanCancelSubscription,
      reference: paymentLinkObj?.reference || ''
    });
  };

  const handleSubmit = (values: PaymentLinkFormII) => {
    if (!shouldBusinessMakeTransactions) {
      handleVeriffModalOpen();
      return;
    }

    if (isEdit) {
      if (!shouldDisableForm) {
        handleEdit(values);
      }
    } else {
      handleAdd(values);
    }
  };

  const validationSchema = getRecurrentPaymentLinkValidationSchema();

  const buttonLabel = isEdit ? 'Update payment link' : 'Create payment link';
  const title = isEdit ? 'Edit payment link' : 'Create new payment link';
  const isLoading = addSettlementLoading || updatePaymentLinkLoading;

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    buttonLabel,
    title,
    isLoading,
    primarySettlementAccount,
    primarySettlementAccountError,
    primarySettlementAccountLoading,
    primarySettlementAccountRefetch,
    shouldDisableForm,
    showVeriffModal,
    handleVeriffModalClose
  };
};

export default useRecurrentPaymentLinkForm;
